<template>
  <div id="app">
    <!-- Landing Page -->
    <LandingPage 
      v-if="currentView === 'landing'"
      @enter-tool="navigateToView('dashboard')"
    />
    
    <!-- Navigation Dashboard -->
    <NavigationDashboard 
      v-if="currentView === 'dashboard'"
      @navigate="handleNavigation"
    />
    
    <!-- Project Creation/Edit -->
    <ProjectCreation 
      v-if="currentView === 'create-project' || currentView === 'edit-project'"
      :editProjectId="editProjectId"
      @navigate="handleNavigation"
      @assessment-complete="handleAssessmentComplete"
    />
    
    <!-- Project Assessment Dashboard -->
    <ProjectAssessmentDashboard 
      v-if="currentView === 'project-dashboard'"
      @navigate="handleNavigation"
    />
    
    <!-- Pipeline Analytics -->
    <PipelineAnalytics 
      v-if="currentView === 'pipeline-analytics'"
      @navigate="handleNavigation"
    />
    
    <!-- Report Generator -->
    <ReportGenerator 
      v-if="currentView === 'report-generator'"
      @navigate="handleNavigation"
    />
    
    <!-- Project List -->
    <ProjectList 
      v-if="currentView === 'project-list'"
      @navigate="handleNavigation"
    />
    
    <!-- View Project -->
    <ViewProject 
      v-if="currentView === 'view-project'"
      :projectId="viewProjectId"
      @navigate="handleNavigation"
    />
  </div>
</template>

<script>
import LandingPage from './components/LandingPage.vue'
import NavigationDashboard from './components/NavigationDashboard.vue'
import ProjectCreation from './components/ProjectCreation.vue'
import ProjectAssessmentDashboard from './components/ProjectAssessmentDashboard.vue'
import PipelineAnalytics from './components/PipelineAnalytics.vue'
import ReportGenerator from './components/ReportGenerator.vue'
import ProjectList from './components/ProjectList.vue'
import ViewProject from './components/ViewProject.vue'

export default {
  name: 'App',
  components: {
    LandingPage,
    NavigationDashboard,
    ProjectCreation,
    ProjectAssessmentDashboard,
    PipelineAnalytics,
    ReportGenerator,
    ProjectList,
    ViewProject
  },
  data() {
    return {
      currentView: 'landing',
      editProjectId: null,
      viewProjectId: null,
      navigationHistory: []
    }
  },
  methods: {
    navigateToView(view, params = {}) {
      // Store current view in history for back navigation
      if (this.currentView !== view) {
        this.navigationHistory.push(this.currentView)
      }
      
      this.currentView = view
      
      // Handle parameters for specific views
      if (params.projectId) {
        if (view === 'edit-project') {
          this.editProjectId = params.projectId
        } else if (view === 'view-project') {
          this.viewProjectId = params.projectId
        }
      }
      
      // Clear parameters when leaving relevant views
      if (view !== 'edit-project') {
        this.editProjectId = null
      }
      if (view !== 'view-project') {
        this.viewProjectId = null
      }
    },
    
    handleNavigation(destination, params = {}) {
      switch (destination) {
        case 'landing':
          this.navigateToView('landing')
          break
        case 'dashboard':
          this.navigateToView('dashboard')
          break
        case 'create-project':
          this.navigateToView('create-project')
          break
        case 'edit-project':
          this.navigateToView('edit-project', params)
          break
        case 'project-dashboard':
          this.navigateToView('project-dashboard')
          break
        case 'pipeline-analytics':
          this.navigateToView('pipeline-analytics')
          break
        case 'report-generator':
          this.navigateToView('report-generator')
          break
        case 'project-list':
          this.navigateToView('project-list')
          break
        case 'view-project':
          this.navigateToView('view-project', params)
          break
        case 'back':
          this.goBack()
          break
        default:
          console.warn('Unknown navigation destination:', destination)
      }
    },
    
    handleAssessmentComplete(projectData) {
      // Save completed assessment
      const completedProject = {
        id: Date.now().toString(),
        ...projectData,
        completedAt: new Date().toISOString(),
        status: 'completed'
      }
      
      // Store in localStorage (in real app, send to backend)
      localStorage.setItem(`completed_project_${completedProject.id}`, JSON.stringify(completedProject))
      
      // Navigate to project dashboard to view results
      this.navigateToView('project-dashboard')
      
      // Show success message
      this.$nextTick(() => {
        alert('Project assessment completed successfully!')
      })
    },
    
    goBack() {
      if (this.navigationHistory.length > 0) {
        const previousView = this.navigationHistory.pop()
        this.currentView = previousView
      } else {
        this.navigateToView('dashboard')
      }
    }
  },
  mounted() {
    // Check if user should bypass landing page (e.g., returning user)
    const hasVisited = localStorage.getItem('kpmg_ppp_tool_visited')
    if (hasVisited) {
      // Optionally start at dashboard for returning users
      // this.currentView = 'dashboard'
    }
    
    // Mark as visited
    localStorage.setItem('kpmg_ppp_tool_visited', 'true')
    
    // Handle browser back/forward buttons
    window.addEventListener('popstate', this.goBack)
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.goBack)
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #333;
  min-height: 100vh;
  background: #f8f9fa;
}

/* Global styles for consistent design */
.btn-primary {
  background: rgb(0, 51, 141);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 1rem;
}

.btn-primary:hover {
  background: rgb(12, 35, 60);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 51, 141, 0.3);
}

.btn-secondary {
  background: transparent;
  color: rgb(0, 51, 141);
  border: 2px solid rgb(0, 51, 141);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 1rem;
}

.btn-secondary:hover {
  background: rgb(0, 51, 141);
  color: white;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Utility classes */
.text-center {
  text-align: center;
}

.text-left {
  text-align: left;
}

.text-right {
  text-align: right;
}

.mb-1 { margin-bottom: 0.5rem; }
.mb-2 { margin-bottom: 1rem; }
.mb-3 { margin-bottom: 1.5rem; }
.mb-4 { margin-bottom: 2rem; }

.mt-1 { margin-top: 0.5rem; }
.mt-2 { margin-top: 1rem; }
.mt-3 { margin-top: 1.5rem; }
.mt-4 { margin-top: 2rem; }

/* Loading animation */
.loading {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 51, 141, 0.3);
  border-radius: 50%;
  border-top-color: rgb(0, 51, 141);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive design */
@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}
</style>
